/**
 * Machine detail — capabilities + sessions for one machine, restyled
 * with the phase-7 kit inside the responsive DashShell (Swarm tab stays the
 * highlighted destination). All phase-1/2 behavior is unchanged: 10s
 * poll, GET /machines/{id} with list fallback for older planes.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import "../dashboard.css";
import { useAuth } from "../hooks/useAuth";
import { DashShell, SecLabel } from "../components/DashShell";
import { SessionList } from "../components/SessionList";
import {
  asNamed,
  getMachine,
  getMachines,
  getMachineSessions,
  machineRuntimes,
  type Machine,
  type Session,
} from "../lib/api";
import { timeAgo } from "../lib/format";
import { Card, StatusPill, MonoChip, Icon, I } from "../kit";

const POLL_MS = 10_000;

interface CapRow {
  name: string;
  description?: string;
  status?: string;
}

/** Table-style capability rows: name → description → status (contract §5). */
function CapTable({
  title,
  rows,
  empty,
}: {
  title: string;
  rows: CapRow[];
  empty: string;
}) {
  return (
    <div>
      <SecLabel
        action={
          <span className="mono" style={{ fontSize: 11, color: "var(--color-fg-4)" }}>
            {rows.length}
          </span>
        }
      >
        {title}
      </SecLabel>
      {rows.length === 0 ? (
        <Card>
          <p className="hint" style={{ margin: 0 }}>
            {empty}
          </p>
        </Card>
      ) : (
        <Card pad={0}>
          <div className="dsh-thead">
            <span>Name</span>
            <span>Description</span>
            <span>Status</span>
          </div>
          {rows.map((r) => (
            <div className="dsh-trow" key={r.name}>
              <span className="name">{r.name}</span>
              <span className="desc">{r.description || "—"}</span>
              <span className="status">{r.status ?? ""}</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

export default function MachineDetail() {
  const { id } = useParams<{ id: string }>();
  const { status, user, signOut } = useAuth();
  const navigate = useNavigate();
  const [machine, setMachine] = useState<Machine | null>(null);
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!id) return;
    try {
      // GET /machines/{id} is new in phase 2 — fall back to the list while
      // an older plane is still deployed.
      const m = await getMachine(id).catch(async () => {
        const found = (await getMachines()).find((x) => x.machineId === id);
        if (!found) throw new Error("machine not found");
        return found;
      });
      setMachine(m);
      setSessions(await getMachineSessions(id).catch(() => []));
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "failed to load");
    }
  }, [id]);

  useEffect(() => {
    if (status === "signed-out") navigate("/", { replace: true });
  }, [status, navigate]);

  useEffect(() => {
    if (status !== "signed-in") return;
    refresh();
    const iv = setInterval(refresh, POLL_MS);
    return () => clearInterval(iv);
  }, [status, refresh]);

  const skills = (machine?.capabilities?.skills ?? []).map(asNamed);
  const agents = (machine?.capabilities?.agents ?? []).map(asNamed);
  // Phase 12 §T9: runtime chips on the head card. Only when the machine
  // serves something beyond plain Claude — Claude-only machines stay clean.
  const runtimes = machine ? machineRuntimes(machine) : ["claude"];
  const showRuntimes = runtimes.some((r) => r !== "claude");
  const mcps = (machine?.capabilities?.mcps ?? []).map((m) => ({
    name: m.name,
    // Phase-4 scanners report scope (+ source path for project/local);
    // pre-upgrade daemons omit both — show the dash like other empty descs.
    description: m.scope
      ? `scope ${m.scope}${m.source ? ` · ${m.source}` : ""}`
      : undefined,
    status: `${m.status}${m.transport ? ` · ${m.transport}` : ""}`,
  }));

  return (
    <DashShell
      active="swarm"
      crumb={machine?.name ?? "machine"}
      userEmail={user?.email}
      entitlements={user?.ownedEntitlements}
      onSignOut={signOut}
    >
      <Link to="/dashboard/swarm" className="dsh-backlink">
        <Icon d={I.chevL} size={14} /> swarm
      </Link>

      {err && <p className="dsh-hint-error">Couldn't load machine ({err})</p>}

      {machine === null ? (
        !err && <p className="hint">Loading machine…</p>
      ) : (
        <div className="dsh-stack">
          <Card className="dsh-headcard">
            <span className="dsh-tile">
              <Icon d={I.cpu} size={17} />
            </span>
            <div className="grow">
              <div className="title">{machine.name}</div>
              <div className="id">{machine.machineId}</div>
              {showRuntimes && (
                <div className="dsh-chiprow" style={{ marginTop: 8 }}>
                  {runtimes.map((r) => (
                    <span key={`rt-${r}`} title={`runtime · ${r}`}>
                      <MonoChip tone="accent">{r}</MonoChip>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <span className="meta">last seen {timeAgo(machine.lastSeenAt)}</span>
            <StatusPill status={machine.status} />
          </Card>

          <CapTable
            title="Skills"
            rows={skills}
            empty="No skills reported by this machine's daemon."
          />
          <CapTable
            title="Agents"
            rows={agents}
            empty="No agents reported by this machine's daemon."
          />
          <CapTable
            title="MCPs"
            rows={mcps}
            empty="No MCP servers reported by this machine's daemon."
          />

          <div>
            <SecLabel
              action={
                <span className="mono" style={{ fontSize: 10.5, color: "var(--color-fg-4)" }}>
                  runs on this machine
                </span>
              }
            >
              Sessions
            </SecLabel>
            {sessions === null ? (
              <p className="hint">Loading sessions…</p>
            ) : sessions.length === 0 ? (
              <Card>
                <p className="hint" style={{ margin: 0 }}>
                  No sessions on this machine yet.
                </p>
              </Card>
            ) : (
              <Card pad={0}>
                <SessionList
                  sessions={sessions}
                  machineName={() => machine.name}
                />
              </Card>
            )}
          </div>
        </div>
      )}
    </DashShell>
  );
}
